import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScreenTitle from '../../common/components/screenTitle/ScreenTitle';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import styles from './ImportChargesScreen.module.scss';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { DATE_TIME_FORMAT_DEFAUT, DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import { useHistory } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import { ImportExpenseFileDto } from 'api/import-charges/models/ImportExpenseFileDto';
import { ImportExpenseFilesSearchCriteria } from 'api/import-charges/models/ImportExpenseFilesSearchCriteria';
import { Paged } from 'api/common/types/Page';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { FaEllipsisH } from 'react-icons/fa';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import ImportChargeService from 'api/import-charges/ImportChargeService';
import Loading from 'common/services/Loading';
import DateFormat from 'common/components/dateFormat/dateFormat';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Popover from 'common/components/popover/Popover';
import ImportChargeFiltersScreen, { Filters } from './components/filters/ImportChargeFiltersScreen';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import ReactTooltip from 'react-tooltip';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce/lib';
import { CompactPageLayout } from 'common/components/compactPageLayout';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';

export type Props = {
    criteriaProp?: ImportExpenseFilesSearchCriteria;
} & React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const ImportChargesScreen = ({ criteriaProp, ...props }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    const [importExpenseFilePage, setImportExpenseFilePage] = useState<Paged<ImportExpenseFileDto> | null>(null);
    const [criteria, setCriteria] = useState<ImportExpenseFilesSearchCriteria>(!criteriaProp ? {
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'date',
        orderBy: 'desc'
    } : criteriaProp);

    const [itemToRemove, setItemToRemove] = useState<ImportExpenseFileDto | null>(null);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasChargesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_IMPORT_EXPENSES_WRITE']);

    const isFirstLoad = useRef(true);
    const filterJsonKey = 'IMPORT_CHARGES_FILTER';

    const getData = async () => {
        try {
            Loading.show();

            saveOrdernationFilters();

            const files = await ImportChargeService.getList(criteria);
            setImportExpenseFilePage(files);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't get import expense file`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
        finally {
            Loading.hide();
        }
    };

    useEffect(() => {
        if (isFirstLoad.current == true) {
            loadFilters()
        } else {
            getData()
        }
    }, [criteria]);

    const onClickItem = (event: any, item: ImportExpenseFileDto, isDetails: boolean) => {
        if (event.ctrlKey) {
            window.open(`/import-charges/${isDetails ? 'details' : 'edit'}/${item.id}`, '_blank');
            return;
        }
        history.push(`/import-charges/${isDetails ? 'details' : 'edit'}/${item.id}`);
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await ImportChargeService.remove(itemToRemove);
            setCriteria({ ...criteria, page: 1 });
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete import charges file`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const renderTableActionCell = (
        row: ImportExpenseFileDto,
    ) => {
        const urlDetails = `import-charges/details/${row.id}`;

        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={urlDetails}>
                            {t('common.details')}
                        </DropdownItem>
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<ImportExpenseFileDto>[] = [
        {
            name: t('import_charges.date_importation'),
            renderCell: row => <div>
                <DateFormat value={row.date} format={DATE_TIME_FORMAT_DEFAUT} />
            </div>,
            width: 'fill',
            hideOn: ['sm'],
            searchField: 'date',
            onSearch: onTableFilter
        },
        {
            name: t('common.name'),
            renderCell: row => <div>{row.fileName}</div>,
            searchField: 'file_name',
            onSearch: onTableFilter
        },
        {
            name: t('common.supplier'),
            renderCell: row => <>
                <div>
                    {row.supplierUrl && <img src={row.supplierUrl} style={{ height: '1.25rem' }}
                        data-tip={`${row.supplierName || '-'}` as any} data-for={'supplier_name_' + row.id}></img>}
                    {!row.supplierUrl && (row.supplierName || '-')}
                    <ReactTooltip id={'supplier_name_' + row.id} />
                </div>
            </>,
            hideOn: ['sm', 'md', 'lg', 'xl'],
            searchField: 'supplier.name',
            onSearch: onTableFilter
        },
        {
            name: t('import_charges.status'),
            renderCell: row => <div>{t(('import_charges.process_status.' + row.status) as any)}</div>,
            searchField: 'status',
            onSearch: onTableFilter
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' },
            hideOn: ['sm', 'md']
        }
    ];

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        newFilters.page = criteria.page;
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);
    }

    const onPageChange = (page: number) => {
        setCriteria({ ...criteria, page: page });
    }

    const onChangeFilters = (f: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(f);
        saveFiltersOnCache(f);
        setCriteria(prev => ({
            ...prev,
            ...f,
            page: (isOnLoad && f.page) ? f.page : 1,
        }))
    }

    const updateTotalFilters = (f: Filters) => {
        let total = 0;

        if (f.startDate || f.endDate) {
            total++;
        }

        if (f.supplierId && f.supplierId.length > 0) {
            total++;
        }

        if (f.status) {
            total++;
        }

        setFiltersTotal(total);
    }

    const updateFilters = (items: { key: string; value: any }[]) => {
        const newFilters = JSON.parse(JSON.stringify(filters))
        items.forEach((item: { key: string; value: any }) => {
            newFilters[item.key] = item.value
        })

        onChangeFilters(newFilters)
    }

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }))
        localStorage.setItem(filterJsonKey, JSON.stringify(f));
    }

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(filterJsonKey);
        if (cachedFilters != null) { onChangeFilters(JSON.parse(cachedFilters), true) }
        else { getData() }

        isFirstLoad.current = false;
    }

    const debounced2 = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        filters.allIn = value;
        saveFiltersOnCache(filters)
        setCriteria({ ...criteria, page: 1, allIn: value })
    }, 500);

    // FilterBadgesOverflow items
    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (filters.supplierName) {
            items.push({
                key: 'supplier',
                label: t('common.supplier') as string,
                value: filters.supplierName,
                onRemove: () => updateFilters([{ key: 'supplierId', value: undefined }, { key: 'supplierName', value: undefined }])
            });
        }

        if (filters.status) {
            items.push({
                key: 'status',
                label: t('import_charges.status') as string,
                value: t(('import_charges.process_status.' + filters.status) as any) as string,
                onRemove: () => updateFilters([{ key: 'status', value: undefined }])
            });
        }

        if (filters.startDate || filters.endDate) {
            let value = '';
            if (filters.startDate && filters.endDate) {
                value = `${new Date(filters.startDate).toLocaleDateString()} - ${new Date(filters.endDate).toLocaleDateString()}`;
            } else if (filters.startDate) {
                value = `${t('common.range.from')} ${new Date(filters.startDate).toLocaleDateString()}`;
            } else if (filters.endDate) {
                value = `${t('common.range.to')} ${new Date(filters.endDate).toLocaleDateString()}`;
            }
            items.push({
                key: 'dates',
                label: t('charges.filter_date') as string,
                value,
                onRemove: () => updateFilters([{ key: 'startDate', value: undefined }, { key: 'endDate', value: undefined }])
            });
        }

        return items;
    }, [filters, t]);

    return (
        <ScreenTitle title={t('import_charges.title')} {...props}>
            <CompactPageLayout
                title={t('import_charges.title') as string}
                contentAreaClassName={styles.contentArea}
                onBack={() => history.push('/charges')}
                middleContent={
                    <>
                        <div className={styles.searchContainer}>
                            <InputSearch
                                onChangeValue={debounced2}
                                placeholder={t('import_charges.search_placeholder')}
                                defaultValue={criteria.allIn}
                            />
                        </div>
                        <FilterBadgesOverflow items={filterItems} align="right" />
                    </>
                }
                headerButtons={
                    <>
                        <Popover
                            contentContainerClassName={styles.filtersPopoverContainer}
                            containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                            positions={['bottom', 'left']}
                            align={'end'}
                            onClickOutside={() => updateTotalFilters(filters)}
                            content={setIsPopoverOpen => <ImportChargeFiltersScreen
                                filters={filters}
                                onFilter={f => { setIsPopoverOpen(false); onChangeFilters(f) }}
                                onChange={f => updateTotalFilters(f)}
                            />}>
                            {(isPopoverOpen, setIsPopoverOpen) => (
                                <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                    {filtersTotal > 0 &&
                                        <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                    }
                                </ScreenHeaderButton>
                            )}
                        </Popover>
                        {hasChargesWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => history.push(`/import-charges/new`)} />}
                    </>
                }
            >
                {isFirstLoad.current === false && <ListingTable
                    columns={tableColumns}
                    rows={importExpenseFilePage?.items ?? []}
                    onRowClick={(row, index, event) => onClickItem(event, row, true)}
                    allowHover={true}
                    initialSearch={{ colField: criteria.orderColumn ?? 'c.date', isOrderAsc: (criteria.orderBy && criteria.orderBy == 'asc') ? true : false }}
                />}
                <PaginationWithInfo
                    className={styles.pagination}
                    itemName={t('import_charges.list.files')}
                    currentPage={importExpenseFilePage?.currentPage ?? 1}
                    pageItems={importExpenseFilePage?.items.length ?? 0}
                    totalItems={importExpenseFilePage?.totalItems ?? 0}
                    onChange={onPageChange} />

                <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('common.messages.remove_record_with_ident', { name: itemToRemove?.fileName ?? '' })} />
            </CompactPageLayout>
        </ScreenTitle>
    );
}

export default ImportChargesScreen;
